import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * Il foglio che entra dal basso: velo, maniglia, trascinamento per chiudere.
 *
 * È il modo con cui questa app apre le scelte e i pannelli. Sta qui e non
 * duplicato nei chiamanti perché il gesto dev'essere lo stesso ovunque: un
 * foglio che si trascina e uno che non si trascina insegnano due cose diverse
 * sulla stessa forma, e chi ha imparato la prima si blocca sulla seconda.
 */
export function Sheet({
  open,
  onClose,
  label,
  children,
  className,
  handle,
  above,
  bounce = false,
}: {
  open: boolean;
  onClose: () => void;
  /** Titolo accessibile del pannello (non stampato: lo fa il contenuto). */
  label?: string;
  children: ReactNode;
  className?: string;
  /**
   * Sostituisce la maniglia grigia predefinita.
   *
   * Esiste per lo Zaino, che al posto del trattino porta una linguetta con
   * scritta e freccia. È un rimpiazzo e non un'aggiunta: due maniglie sullo
   * stesso pannello direbbero che ci sono due cose da afferrare.
   */
  handle?: ReactNode;
  /**
   * Contenuto che sporge SOPRA il pannello, fuori dall'area che scorre.
   *
   * Serve alla linguetta dello Zaino: dentro il pannello sarebbe ritagliata
   * dallo scorrimento e scorrerebbe via col contenuto, mentre una linguetta
   * deve restare dov'è — è la parte che si afferra.
   */
  above?: ReactNode;
  /**
   * Entrata ed uscita con sovraelongazione, invece dello scivolamento neutro.
   *
   * Serve quando il pannello rappresenta un OGGETTO e non una superficie: un
   * oggetto ha una massa, e si ferma dopo che si è fermata la mano.
   */
  bounce?: boolean;
}) {
  const haptics = useHaptics();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ y: number; t: number } | null>(null);
  const [dragY, setDragY] = useState(0);
  const [dropping, setDropping] = useState(false);

  const CLOSE_PX = 110;
  const FLICK_MS = 260;
  const FLICK_PX = 40;
  /**
   * Durata dell'uscita. Deve combaciare con l'animazione che la disegna —
   * la transizione del `transform` nel caso neutro, il keyframe `pack-down`
   * nel caso con rimbalzo, che è più lento perché prima anticipa.
   */
  const EXIT_MS = bounce ? 300 : 220;

  // Il tasto indietro (Android, e la freccia di Telegram) chiude il pannello
  // invece di uscire dalla schermata: un foglio aperto è uno stato, e quello è
  // il gesto per annullarlo.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /**
   * Il foglio resta montato per tutta l'uscita.
   *
   * `open` è la volontà di chi ci sta sopra; `visible` è cosa c'è sullo schermo,
   * e le due cose divergono per la durata dell'animazione. Senza questo scarto
   * chiudere significava smontare, cioè sparire di colpo — e una cosa che
   * scompare senza transizione si legge come un guasto, non come una chiusura.
   *
   * Questo copre TUTTE le vie di chiusura, non solo il tocco sul velo: anche
   * scegliere una voce da un filtro, o premere il tasto dentro il pannello,
   * passano da qui. Prima quelle uscivano di netto, perché portavano `open` a
   * falso direttamente e l'animazione stava solo nel percorso interno.
   */
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (!open) return;
    setVisible(true);
    setDropping(false);
    setDragY(0);
    dragRef.current = null;
  }, [open]);

  useEffect(() => {
    if (open || !visible) return;
    setDropping(true);
    const id = window.setTimeout(() => {
      setVisible(false);
      setDropping(false);
      setDragY(0);
      dragRef.current = null;
    }, EXIT_MS);
    return () => window.clearTimeout(id);
  }, [open, visible]);

  if (!visible) return null;

  // Chiudere significa DICHIARARLO a chi sta sopra, non animare qui: l'uscita
  // la fa l'effetto qui sopra quando `open` diventa falso. Un secondo percorso
  // d'animazione interno era esattamente il motivo per cui le altre vie di
  // chiusura non ne avevano nessuna.
  const close = () => onClose();

  /**
   * Il trascinamento parte SOLO se il contenuto è già in cima.
   *
   * Scorrere il contenuto verso l'alto e trascinare via il foglio sono lo
   * stesso identico gesto — dito giù, dito su — e a distinguerli c'è solo il
   * contesto. Senza questo controllo il foglio se ne andrebbe a metà lettura.
   */
  const onTouchStart = (e: React.TouchEvent) => {
    const el = sheetRef.current;
    if (!el || el.scrollTop > 0) return;
    const t = e.touches[0];
    if (t) dragRef.current = { y: t.clientY, t: Date.now() };
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const start = dragRef.current;
    const t = e.touches[0];
    if (!start || !t) return;
    // Solo verso il basso: tirando in su il foglio si staccherebbe dal bordo
    // lasciando una fessura sotto.
    setDragY(Math.max(0, t.clientY - start.y));
  };

  const onTouchEnd = () => {
    const start = dragRef.current;
    dragRef.current = null;
    if (!start) return;
    // Uno scatto breve ma deciso vale quanto un trascinamento lungo: chi
    // conosce il gesto lo fa in fretta e non si aspetta di dover arrivare a
    // metà schermo.
    const flick = Date.now() - start.t < FLICK_MS && dragY > FLICK_PX;
    if (dragY > CLOSE_PX || flick) {
      haptics.impact('light');
      close();
      return;
    }
    setDragY(0);
  };

  return (
    // Colonna e non riga: `above` deve impilarsi SOPRA il pannello, non
    // affiancarglisi. Per un pannello solo il risultato è identico a prima —
    // resta in basso, centrato, largo quanto può.
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-end"
      role="dialog"
      aria-modal
      aria-label={label}
    >
      {/* Il velo chiude toccando fuori: su un foglio senza «annulla» è il modo
          che si prova per primo.
          Si dissolve INSIEME al foglio: prima restava opaco per tutta l'uscita e
          poi spariva con lo smontaggio, quindi lo schermo si schiariva di scatto
          a pannello già uscito — l'unico fotogramma che si notava era proprio
          quello sbagliato. */}
      <button
        type="button"
        aria-label="Chiudi"
        onClick={close}
        className="absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out"
        style={{ opacity: dropping ? 0 : 1 }}
      />

      {/* UN SOLO blocco in movimento: la linguetta e il pannello stanno dentro
          lo stesso contenitore, ed e' LUI che si anima.

          ⚠️ Prima si animavano separatamente, con lo stesso keyframe applicato
          a entrambi — ma `pack-up`/`pack-down` traslano del 100% dell'ALTEZZA
          DELL'ELEMENTO, e i due hanno altezze incomparabili: il pannello e'
          86vh, la linguetta una quarantina di pixel. Cosi' il pannello usciva
          sotto lo schermo mentre la linguetta scendeva di quattro dita e
          restava li', sparendo poi di colpo allo smontaggio. E' il difetto
          descritto come «la linguetta resta in alto e poi cade».
          Una cinghia cucita a uno zaino non puo' muoversi meno dello zaino:
          sono lo stesso oggetto, e ora hanno lo stesso movimento perche' sono
          lo stesso elemento animato. */}
      <div
        // Il trascinamento sta sul BLOCCO, non sul solo pannello: così si può
        // afferrare anche per la linguetta, che è la parte che si afferra per
        // definizione. Prima i gesti erano sul pannello e la cinghia aveva una
        // logica sua a soglia — tirandola non seguiva la mano, e a un certo
        // punto lo zaino si chiudeva di scatto.
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="flex w-full flex-col items-center"
        style={{
          // Mentre il dito è giù NIENTE transizione: il foglio deve stare sotto
          // il polpastrello, non inseguirlo con un ritardo. Torna al rilascio,
          // per il rientro o l'uscita.
          //
          // Col rimbalzo il `transform` finale lo dà il keyframe, non questa
          // proprietà: scriverlo qui in uscita lo farebbe saltare a destinazione
          // prima che l'anticipo faccia in tempo a vedersi.
          transform: dropping && !bounce ? 'translateY(100%)' : `translateY(${dragY}px)`,
          transition: dragRef.current
            ? undefined
            : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
          // L'animazione d'ingresso solo alla prima comparsa: se restasse attiva
          // ripartirebbe a ogni render durante il trascinamento.
          animation: dropping
            ? bounce
              ? 'pack-down 300ms ease-in forwards'
              : undefined
            : dragY === 0
              ? bounce
                ? 'pack-up 460ms cubic-bezier(0.2, 0.9, 0.3, 1)'
                : 'sheet-up 260ms cubic-bezier(0.22, 1, 0.36, 1)'
              : undefined,
        }}
      >
        {/* `z-[1]`: sporge SOPRA il pannello, che viene dopo nel documento e
            altrimenti gli passerebbe davanti. */}
        {above && <div className="relative z-[1] flex justify-center">{above}</div>}

        <div
          ref={sheetRef}
          className={cn(
            'relative max-h-[80vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-bg-secondary',
            // Lo spazio della barra di scorrimento è SEMPRE riservato.
            // Senza, cambiando contenuto il pannello passa da scorrevole a non
            // scorrevole, la barra compare e sparisce e tutto quello che c'è
            // dentro si sposta di una decina di pixel: è il salto che si vede
            // passando da Inventario (corto) a Impostazioni (lungo), dove le
            // pastiglie delle tasche sembrano disallinearsi.
            // Su mobile le barre sono sovrapposte e non occupano spazio, quindi
            // lì questa riga non toglie nulla.
            '[scrollbar-gutter:stable]',
            'pb-[max(1rem,env(safe-area-inset-bottom))]',
            className,
          )}
        >
          {/* Maniglia: dice che il foglio si può afferrare. Sta fuori dal
              contenuto perché vale per ogni foglio, qualunque cosa contenga.
              Chi passa la propria la SOSTITUISCE: due maniglie sullo stesso
              pannello direbbero che ci sono due cose da afferrare. */}
          {handle ?? (
            <div className="sticky top-0 z-10 bg-bg-secondary pt-3">
              <span aria-hidden className="mx-auto block h-1 w-10 rounded-full bg-fg-muted/40" />
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}
